import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AddButton, Field, LiveScreen, QueryState, Select, StatusPill,
  formatCurrency, formatDate, formatNumber, todayIso, useFormErrors, type Column,
} from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { RecordDrawer } from '@/components/RecordDrawer';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows, useSaveRow } from '@/hooks/useResource';
import { callRpc } from '@/api/resources';
import { plotOptions, referenceValues, unitOptions } from '@/api/lookups';
import { signedUrlsFor, uploadDocument } from '@/api/documents';
import { DropZone } from '@/components/DropZone';
import { ValidityBadge } from '@/components/ValidityBadge';
import { useAuth } from '@/auth/AuthProvider';
import { can, isAdmin } from '@/auth/session';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';
import type { HarvestRow, InventoryRow, PlotFeatureRow, StockRow } from '@/types/records';
import { HarvestEvidence } from '@/screens/parts/HarvestEvidence';
import { FSC_CLAIMS, optionLabel } from '@/lib/practiceLists';
import { lineNumber, useSpecForm, expandLines, type FieldSpec, type FormLine, type FormValues } from '@/components/ChildRecords';
import { PLANTING_PATTERNS, PRODUCE_TYPES, PRODUCTION_SYSTEMS, standingVolume } from '@/lib/treeStand';

type Tab = 'stock' | 'trees' | 'assets' | 'harvest' | 'species';

interface SpeciesMeasure {
  id: string; label: string; measure_by: 'count' | 'volume'; volume_unit_id: string | null;
  common_name: string | null; scientific_name: string | null; production_unit_id: string | null;
}

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'stock', label: 'Net stock' },
  { id: 'trees', label: 'Tree inventory' },
  { id: 'assets', label: 'Asset inventory' },
  { id: 'harvest', label: 'Harvest' },
  { id: 'species', label: 'Species' },
];

const MATURITY = [
  { id: 'immature', label: 'Immature' }, { id: 'semi_mature', label: 'Semi mature' },
  { id: 'mature', label: 'Mature' }, { id: 'over_mature', label: 'Over mature' },
];
const COUNT_METHOD = [
  { id: 'full_count', label: 'Full count' }, { id: 'sample', label: 'Sample' },
  { id: 'estimate', label: 'Estimate' }, { id: 'not_applicable', label: 'Not applicable' },
];
const HARVEST_TYPE = [
  { id: 'thinning', label: 'Thinning' }, { id: 'selective', label: 'Selective' },
  { id: 'final_felling', label: 'Final felling' }, { id: 'salvage', label: 'Salvage' },
  { id: 'other', label: 'Other' },
];
const CONDITION = [
  { id: 'good', label: 'Good' }, { id: 'fair', label: 'Fair' },
  { id: 'poor', label: 'Poor' }, { id: 'unusable', label: 'Unusable' },
];

/**
 * What stands on a plot, what is on it, and what has come off it.
 *
 * Net stock is DERIVED, never stored: the database subtracts approved harvests from the tree
 * inventory, and counts only harvests dated after the inventory was observed - a re-count
 * taken after a felling already reflects it, so subtracting again would double-count. The
 * screen therefore never computes a balance of its own; it shows what the view returns.
 */
export default function InventoryScreen() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('stock');
  const [stat, setStat] = useState<string | null>(null);
  const [plotId, setPlotId] = useState('');
  const [drawer, setDrawer] = useState<null | 'tree' | 'asset' | 'harvest'>(null);
  // A tree count opened from the list, to correct or complete it while it is still a draft.
  const [editingTree, setEditingTree] = useState<InventoryRow | null>(null);
  const [saving, setSaving] = useState(false);
  const { errors, check, clear } = useFormErrors();

  const [asset, setAsset] = useState({ name: '', type_id: '', quantity: '', quantity_unit_id: '',
                                       condition_status: 'good', acquired_on: '', estimated_value: '', description: '' });
  const [harvest, setHarvest] = useState({ species_id: '', harvest_date: todayIso(), harvest_type: 'selective',
                                           trees_harvested: '', estimated_volume: '', volume_unit_id: '', remarks: '',
                                          species_inventory_id: '', quantity: '', quantity_unit_id: '',
                                          permit_valid_from: '', permit_valid_to: '', fsc_claim: '', permit_id: '', internal_permit_id: '' });
  const [evidenceFor, setEvidenceFor] = useState<HarvestRow | null>(null);
  const [newSpecies, setNewSpecies] = useState({ common: '', scientific: '' });
  const [permitFile, setPermitFile] = useState<File | null>(null);

  const plots = useQuery({
    queryKey: ['picker', 'plots'],
    queryFn: () => plotOptions(),
    enabled: isSupabaseConfigured,
  });
  const species = useQuery({
    queryKey: ['ref', 'species'], queryFn: () => referenceValues('species'), enabled: isSupabaseConfigured,
  });
  // How each species is measured (owner, 2026-09-21; migration 0068): counted, or by volume.
  const measures = useRows<SpeciesMeasure>('species_measures', { orderBy: { column: 'label' } });
  const measureOf = (speciesId: string) => measures.data?.find((row) => row.id === speciesId);
  const harvestByVolume = measureOf(harvest.species_id)?.measure_by === 'volume';
  const volumeUnits = useQuery({
    queryKey: ['ref', 'unit', 'volume'], queryFn: () => unitOptions('volume'), enabled: isSupabaseConfigured,
  });

  const filters = plotId ? { plot_id: plotId } : {};
  const plotRow = useRows<{ id: string; farmer_id: string; fsc_claim: string | null; mapped_area_ha: number | null; declared_area_ha: number | null }>(
    'farm_plots', { filters: { id: plotId } }, Boolean(plotId) && (drawer === 'harvest' || drawer === 'tree'));
  // The permits a harvest here may be under: the plot's farmer's, granted and in force, or exempt registrations.
  const permits = useRows<{ id: string; permit_code: string | null; permit_no: string | null; plot_id: string | null; permit_type: string;
                            effective_status: string; valid_from: string | null; valid_to: string | null; permitted_trees: number | null; trees_harvested: number;
                            permit_class: string }>(
    'harvest_permits', { filters: { farmer_id: plotRow.data?.[0]?.farmer_id } }, drawer === 'harvest' && Boolean(plotRow.data?.[0]?.farmer_id));
  const permitsOf = (permitClass: 'government' | 'internal') => (permits.data ?? [])
    .filter((row) => row.permit_class === permitClass && (!row.plot_id || row.plot_id === plotId)
      && (row.effective_status === 'granted' || row.permit_type === 'exempt_registration'))
    .map((row) => ({ id: row.id, label: `${row.permit_no ?? row.permit_code ?? 'Permit'}${row.valid_to ? `, valid to ${row.valid_to}` : ''}${row.permitted_trees != null ? ` (${row.trees_harvested}/${row.permitted_trees} trees used)` : ''}` }));
  const permitOptions = permitsOf('government');
  const internalPermitOptions = permitsOf('internal');
  const plotArea = plotRow.data?.[0]?.mapped_area_ha ?? plotRow.data?.[0]?.declared_area_ha ?? null;
  const plotClaim = plotRow.data?.[0]?.fsc_claim ?? 'none';
  const stock = useRows<StockRow>('plot_stock_summary', { filters }, tab === 'stock');
  const trees = useRows<InventoryRow>('plot_species_inventories', { filters }, tab === 'trees');
  const assets = useRows<PlotFeatureRow>('plot_features', { filters: { ...filters, feature_kind: 'asset' } }, tab === 'assets');
  const harvests = useRows<HarvestRow>('plot_harvest_entries',
    { filters, orderBy: { column: 'harvest_date', ascending: false } }, tab === 'harvest');

  const saveTree = useSaveRow<InventoryRow>('plot_species_inventories');
  const saveAsset = useSaveRow<PlotFeatureRow>('plot_features');
  const saveHarvest = useSaveRow<HarvestRow>('plot_harvest_entries');

  const mayWriteInventory = can(profile, 'plot.inventory', 'create');
  const mayWriteHarvest = can(profile, 'plot.harvest', 'create');
  const maySubmit = can(profile, 'plot.harvest', 'submit');
  const maySubmitInventory = can(profile, 'plot.inventory', 'submit');

  const speciesOptions = (species.data ?? []).map((row) => ({
    id: row.id, label: measureOf(row.id)?.measure_by === 'volume' ? `${row.label} (by volume)` : row.label,
  }));

  async function setMeasure(row: SpeciesMeasure, measureBy: 'count' | 'volume', unitId?: string | null) {
    try {
      await callRpc('fn_set_species_measure', { p_species_id: row.id, p_measure_by: measureBy, p_volume_unit_id: unitId ?? row.volume_unit_id });
      await queryClient.invalidateQueries({ queryKey: ['species_measures'] });
      toast.success(`${row.label}: measured by ${measureBy}.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    }
  }

  /** Names: the list shows "Common (Scientific)" everywhere. */
  async function saveSpecies(row: SpeciesMeasure, common: string, scientific: string) {
    if (!common.trim()) { toast.error('The common name cannot be blank.'); return; }
    if (common.trim() === (row.common_name ?? '') && scientific.trim() === (row.scientific_name ?? '')) return;
    try {
      await callRpc('fn_save_species', { p_species_id: row.id, p_common_name: common.trim(), p_scientific_name: scientific.trim() || null });
      await queryClient.invalidateQueries({ queryKey: ['species_measures'] });
      await queryClient.invalidateQueries({ queryKey: ['ref', 'species'] });
      toast.success('Species name saved.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    }
  }
  async function addSpecies() {
    try {
      await callRpc('fn_save_species', { p_species_id: null, p_common_name: newSpecies.common.trim(), p_scientific_name: newSpecies.scientific.trim() || null });
      setNewSpecies({ common: '', scientific: '' });
      await queryClient.invalidateQueries({ queryKey: ['species_measures'] });
      await queryClient.invalidateQueries({ queryKey: ['ref', 'species'] });
      toast.success('Species added.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be added.');
    }
  }
  async function setProductionUnit(row: SpeciesMeasure, unitId: string) {
    try {
      await callRpc('fn_set_species_production_unit', { p_species_id: row.id, p_unit_id: unitId || null });
      await queryClient.invalidateQueries({ queryKey: ['species_measures'] });
      toast.success(`${row.label}: production unit saved.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    }
  }

  /** Picking a volume species fills its usual unit. */
  function pickSpecies(kind: 'tree' | 'harvest', speciesId: string) {
    const unit = measureOf(speciesId)?.volume_unit_id ?? '';
    if (kind === 'harvest') setHarvest((now) => ({ ...now, species_id: speciesId, species_inventory_id: '', volume_unit_id: now.volume_unit_id || unit }));
  }
  const volumeOptions = (volumeUnits.data ?? []).map((row) => ({ id: row.id, label: row.label }));
  // How much a harvest yielded - weight, pieces or volume (migration 0074).
  const quantityUnits = useQuery({
    queryKey: ['ref', 'unit', 'quantity'], queryFn: () => unitOptions('quantity'), enabled: isSupabaseConfigured,
  });
  const quantityOptions = (quantityUnits.data ?? []).map((row) => ({ id: row.id, label: row.label }));
  // Standing stock is a volume or a weight (owner, 2026-09-27: "the units shall have quintal and
  // ton"): cubic metres or feet, kilograms, quintals, tonnes. Pieces are a harvest's unit, not a stand's.
  const standUnits = (quantityUnits.data ?? []).filter((row) => row.attributes?.kind !== 'count');
  const standUnitOptions = standUnits.map((row) => ({ id: row.id, label: row.label }));
  const unitCode = (id: string | undefined) => standUnits.find((row) => row.id === id)?.value_code ?? (quantityUnits.data ?? []).find((row) => row.id === id)?.value_code;

  /** The tree inventory form: a header, and one line per species across three tables. */
  const thisYear = new Date().getFullYear();
  const plantedYears = Array.from({ length: thisYear - 1949 }, (_, at) => String(thisYear - at)).map((year) => ({ id: year, label: year }));
  const bearingYears = Array.from({ length: thisYear + 30 - 1949 }, (_, at) => String(thisYear + 30 - at)).map((year) => ({ id: year, label: year }));
  const speciesName = (line: FormLine) => speciesOptions.find((option) => option.id === line.species_id)?.label ?? '';
  const standing = (line: FormLine) => lineNumber(line.survival_count) ?? lineNumber(line.tree_count);
  const autoVolume = (line: FormLine) => {
    const volume = standingVolume(lineNumber(line.average_girth_cm), lineNumber(line.average_height_m), standing(line), unitCode(line.volume_unit_id));
    return volume === null ? '' : volume.toFixed(2);
  };
  const treeFields: FieldSpec[] = [
    { key: 'production_system', label: 'Production system', type: 'select', options: PRODUCTION_SYSTEMS,
      hint: 'The land use the trees are part of.' },
    { key: 'last_observed_on', label: 'Observed on', type: 'date', required: true, max: todayIso(),
      hint: 'Harvests after this date are what reduce net stock.' },
    { key: 'count_method', label: 'Count method', type: 'select', options: COUNT_METHOD, required: true },
    { key: 'sample_size', label: 'Sample size', type: 'number', hint: 'Trees actually counted in the sample.',
      showIf: (form) => form.count_method === 'sample' },
    // Owner, 2026-09-27: "species, trees estimation, plantation type, area ... shall be brought as table".
    { key: 'stand', label: 'Species on the plot', type: 'lines', section: 'Stand', required: true, addLineLabel: 'Add species',
      hint: `Area is optional: blank reads the density against the whole plot${plotArea ? ` (${formatNumber(plotArea, 2)} ha)` : ''}. Survival % and trees per hectare are worked out.`,
      defaultLine: { maturity_status: 'immature' },
      columns: [
        { key: 'species_id', label: 'Species', type: 'select', options: speciesOptions, required: true, addTo: 'species', minWidth: 150 },
        { key: 'planting_pattern', label: 'Planting pattern', type: 'select', options: PLANTING_PATTERNS, minWidth: 160 },
        { key: 'planted_area_ha', label: 'Area (ha)', type: 'number' },
        { key: 'plantation_year', label: 'Year planted', type: 'select', options: plantedYears, minWidth: 80 },
        { key: 'tree_count', label: 'Trees counted', type: 'number', total: true },
        { key: 'survival_count', label: 'Surviving', type: 'number', total: true },
        { key: 'survival_pct', label: 'Survival %', type: 'computed', compute: (line) => {
          const counted = lineNumber(line.tree_count); const alive = lineNumber(line.survival_count);
          return counted && alive !== null ? `${((alive / counted) * 100).toFixed(1)}%` : '';
        } },
        { key: 'density', label: 'Trees / ha', type: 'computed', compute: (line) => {
          const trees = standing(line); const area = lineNumber(line.planted_area_ha) ?? plotArea;
          return trees !== null && area ? (trees / area).toFixed(0) : '';
        } },
      ] },
    { key: 'growth', label: 'Growth and standing volume', type: 'lines', linesOf: 'stand', section: 'Growth & volume', rowLabel: speciesName,
      hint: 'Volume is worked out from girth, height and the trees standing (form factor 0.5) when the unit is cubic metres or feet; type a measured figure to replace it. A weight is typed.',
      columns: [
        { key: 'maturity_status', label: 'Maturity', type: 'select', options: MATURITY, minWidth: 130 },
        { key: 'average_girth_cm', label: 'Avg girth (cm)', type: 'number' },
        { key: 'average_height_m', label: 'Avg height (m)', type: 'number' },
        { key: 'volume_unit_id', label: 'Unit', type: 'select', options: standUnitOptions, minWidth: 150 },
        { key: 'estimated_volume', label: 'Standing volume / weight', type: 'number', compute: autoVolume, total: true },
      ] },
    // Owner, 2026-09-27: "need to consider NTFPs also ... like mango fruit etc? -- may be in yield projections?"
    { key: 'yield', label: 'Yield projection (fruit and NTFP)', type: 'lines', linesOf: 'stand', section: 'Yield projection', rowLabel: speciesName,
      hint: 'For species that bear a crop each year - mango fruit, tamarind pods, cashew nuts, latex. Projected yield = trees standing x yield per tree. Leave blank for timber.',
      columns: [
        { key: 'produce_type', label: 'Produce', type: 'select', options: PRODUCE_TYPES, minWidth: 140 },
        { key: 'bearing_from_year', label: 'Bearing from', type: 'select', options: bearingYears, minWidth: 96 },
        { key: 'yield_per_tree', label: 'Yield / tree / year', type: 'number' },
        { key: 'yield_unit_id', label: 'Unit', type: 'select', options: quantityOptions, minWidth: 140 },
        { key: 'projected', label: 'Projected / year', type: 'computed', compute: (line) => {
          const trees = standing(line); const each = lineNumber(line.yield_per_tree);
          if (trees === null || each === null) return '';
          const unit = quantityOptions.find((option) => option.id === line.yield_unit_id)?.label ?? '';
          return `${formatNumber(trees * each, 1)} ${unit}`.trim();
        } },
      ] },
  ];
  const treeForm = useSpecForm(treeFields, { last_observed_on: todayIso(), count_method: 'estimate' });
  function validateTree(form: FormValues): Record<string, string | undefined> {
    const lines = (Array.isArray(form.stand) ? form.stand as FormLine[] : []).filter((line) => line.species_id);
    const seen = new Set<string>();
    for (const [at, line] of lines.entries()) {
      const name = speciesName(line) || `Line ${at + 1}`;
      const byVolume = measureOf(line.species_id!)?.measure_by === 'volume';
      if (!byVolume && lineNumber(line.tree_count) === null) return { stand: `${name}: how many trees were counted?` };
      const counted = lineNumber(line.tree_count); const alive = lineNumber(line.survival_count);
      if (counted !== null && alive !== null && alive > counted) return { stand: `${name}: more trees surviving than were counted.` };
      if (byVolume && !(Number(line.estimated_volume || autoVolume(line)) > 0)) return { growth: `${name} is measured by volume: give the standing volume.` };
      if (byVolume && !line.volume_unit_id && !measureOf(line.species_id!)?.volume_unit_id) return { growth: `${name}: which unit is the volume in?` };
      if (line.estimated_volume && !line.volume_unit_id) return { growth: `${name}: which unit is the volume in?` };
      if (line.yield_per_tree && !line.yield_unit_id) return { yield: `${name}: which unit is the yield in?` };
      const key = `${line.species_id}|${line.plantation_year ?? ''}`;
      if (seen.has(key)) return { stand: `${name} is on two lines with the same year planted: make it one line.` };
      seen.add(key);
    }
    return {};
  }
  // The tree counts this harvest draws on: the plot's counts of the chosen species, latest first.
  const harvestInventories = useRows<InventoryRow>('plot_species_inventories', {
    filters: { plot_id: plotId, species_id: harvest.species_id },
    orderBy: { column: 'last_observed_on', ascending: false },
  }, drawer === 'harvest' && Boolean(plotId) && Boolean(harvest.species_id));
  const inventoryOptions = (harvestInventories.data ?? []).map((row) => ({
    id: row.id,
    label: `Counted ${row.last_observed_on ?? ''}: ${row.tree_count ?? '--'} trees${row.estimated_volume != null ? `, ${row.estimated_volume} vol.` : ''} (${row.status})`,
  }));
  useEffect(() => {
    const latest = harvestInventories.data?.[0]?.id;
    if (latest && !harvest.species_inventory_id) setHarvest((now) => ({ ...now, species_inventory_id: latest }));
  }, [harvestInventories.data, harvest.species_inventory_id]);
  const permitIds = (harvests.data ?? []).map((row) => row.permit_document_id).filter((id): id is string => Boolean(id));
  const permitLinks = useQuery({
    queryKey: ['signed-urls', 'permits', ...permitIds], queryFn: () => signedUrlsFor(permitIds), enabled: permitIds.length > 0,
  });

  const totals = useMemo(() => {
    const rows = stock.data ?? [];
    return {
      standing: rows.reduce((sum, row) => sum + (row.net_standing_trees ?? 0), 0),
      harvested: rows.reduce((sum, row) => sum + (row.harvested_trees ?? 0), 0),
      volume: rows.reduce((sum, row) => sum + Number(row.net_volume ?? 0), 0),
    };
  }, [stock.data]);

  // One status vocabulary per list, taken from each table's CHECK: tree counts can be
  // superseded by a re-count, harvests can be rejected or cancelled. Assets carry no approval
  // lifecycle, so their cards count condition instead.
  const TREE_STATS = useMemo(() => [
    { key: 'all', label: 'Tree counts', match: () => true, tone: 'neutral' as const },
    { key: 'draft', label: 'Draft', tone: 'info' as const, match: (row: InventoryRow) => row.status === 'draft' },
    { key: 'submitted', label: 'Submitted', tone: 'warn' as const, match: (row: InventoryRow) => row.status === 'submitted' },
    { key: 'approved', label: 'Approved', tone: 'ok' as const, match: (row: InventoryRow) => row.status === 'approved' },
    { key: 'superseded', label: 'Superseded', tone: 'info' as const, match: (row: InventoryRow) => row.status === 'superseded' },
  ], []);

  const ASSET_STATS = useMemo(() => [
    { key: 'all', label: 'Assets', match: () => true, tone: 'neutral' as const },
    { key: 'good', label: 'Good', tone: 'ok' as const, match: (row: PlotFeatureRow) => row.condition_status === 'good' },
    { key: 'fair', label: 'Fair', tone: 'info' as const, match: (row: PlotFeatureRow) => row.condition_status === 'fair' },
    { key: 'poor', label: 'Poor', tone: 'warn' as const, match: (row: PlotFeatureRow) => row.condition_status === 'poor' },
    { key: 'unusable', label: 'Unusable', tone: 'error' as const, match: (row: PlotFeatureRow) => row.condition_status === 'unusable' },
  ], []);

  const HARVEST_STATS = useMemo(() => [
    { key: 'all', label: 'Harvests', match: () => true, tone: 'neutral' as const },
    { key: 'draft', label: 'Draft', tone: 'info' as const, match: (row: HarvestRow) => row.status === 'draft' },
    { key: 'submitted', label: 'Submitted', tone: 'warn' as const, match: (row: HarvestRow) => row.status === 'submitted' },
    { key: 'approved', label: 'Approved', tone: 'ok' as const, match: (row: HarvestRow) => row.status === 'approved' },
    { key: 'rejected', label: 'Rejected', tone: 'error' as const, match: (row: HarvestRow) => row.status === 'rejected' },
    { key: 'cancelled', label: 'Cancelled', tone: 'info' as const, match: (row: HarvestRow) => row.status === 'cancelled' },
  ], []);

  const stockColumns: Array<Column<StockRow & { id?: string }>> = [
    { key: 'plot_code', label: 'Plot' },
    { key: 'species_label', label: 'Species', render: (row) => row.species_label ?? '--' },
    { key: 'inventory_trees', label: 'Counted', align: 'right', render: (row) => formatNumber(row.inventory_trees) },
    { key: 'harvested_trees', label: 'Harvested', align: 'right', render: (row) => formatNumber(row.harvested_trees) },
    {
      key: 'net_standing_trees', label: 'Net standing', align: 'right',
      render: (row) => <strong>{formatNumber(row.net_standing_trees)}</strong>,
    },
    {
      key: 'pending', label: 'Pending', align: 'right',
      render: (row) => (row.pending_harvest_trees ? formatNumber(row.pending_harvest_trees) : '--'),
    },
    {
      key: 'net_volume', label: 'Net volume', align: 'right',
      render: (row) => (row.net_volume === null ? '--' : `${formatNumber(row.net_volume, 2)} ${row.volume_unit_label ?? ''}`),
    },
  ];

  const treeColumns: Array<Column<InventoryRow>> = [
    { key: 'species_label', label: 'Species', render: (row) => row.species_label ?? '--' },
    { key: 'plantation_year', label: 'Planted', align: 'right', render: (row) => row.plantation_year ?? '--' },
    { key: 'tree_count', label: 'Counted', align: 'right', render: (row) => formatNumber(row.tree_count) },
    { key: 'survival_count', label: 'Surviving', align: 'right', render: (row) => formatNumber(row.survival_count) },
    { key: 'count_method', label: 'Method', render: (row) => row.count_method.replace(/_/g, ' ') },
    { key: 'maturity_status', label: 'Maturity', render: (row) => <StatusPill value={row.maturity_status} /> },
    { key: 'estimated_volume', label: 'Volume', align: 'right', render: (row) => (row.estimated_volume == null ? '--'
      : `${formatNumber(row.estimated_volume, 2)} ${volumeOptions.find((unit) => unit.id === row.volume_unit_id)?.label ?? ''}`) },
    { key: 'average_girth_cm', label: 'Girth / height', render: (row) => [
      row.average_girth_cm != null ? `${formatNumber(row.average_girth_cm, 0)} cm` : null,
      row.average_height_m != null ? `${formatNumber(row.average_height_m, 1)} m` : null].filter(Boolean).join(' / ') || '--' },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
    {
      key: 'submit',
      label: '',
      render: (row) => (row.status === 'draft' && maySubmitInventory ? (
        <button onClick={(event) => { event.stopPropagation(); void submitInventory(row); }} type="button">Submit</button>
      ) : null),
    },
  ];

  const assetColumns: Array<Column<PlotFeatureRow>> = [
    { key: 'name', label: 'Asset', render: (row) => row.name ?? row.type_label ?? '--' },
    { key: 'quantity', label: 'Quantity', align: 'right',
      render: (row) => (row.quantity === null ? '--' : `${formatNumber(row.quantity, 2)} ${row.quantity_unit_label ?? ''}`) },
    { key: 'condition_status', label: 'Condition', render: (row) => <StatusPill value={row.condition_status} /> },
    { key: 'estimated_value', label: 'Value', align: 'right', render: (row) => formatCurrency(row.estimated_value) },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
  ];

  const harvestColumns: Array<Column<HarvestRow>> = [
    { key: 'harvest_code', label: 'Ref', render: (row) => row.harvest_code ?? '--' },
    { key: 'plot_code', label: 'Plot', render: (row) => row.plot_code ?? '--' },
    { key: 'species_label', label: 'Species', render: (row) => row.species_label ?? '--' },
    { key: 'harvest_date', label: 'Date', render: (row) => formatDate(row.harvest_date) },
    { key: 'harvest_type', label: 'Type', render: (row) => row.harvest_type.replace(/_/g, ' ') },
    { key: 'trees_harvested', label: 'Trees', align: 'right', render: (row) => formatNumber(row.trees_harvested) },
    { key: 'estimated_volume', label: 'Volume', align: 'right', render: (row) => (row.estimated_volume == null ? '--'
      : `${formatNumber(row.estimated_volume, 2)} ${row.volume_unit_label ?? ''}`) },
    { key: 'quantity', label: 'Quantity', align: 'right', render: (row) => (row.quantity == null ? '--'
      : `${formatNumber(row.quantity, 2)} ${row.quantity_unit_label ?? ''}`) },
    { key: 'permit_document_id', label: 'Permit', render: (row) => (row.permit_document_id && permitLinks.data?.[row.permit_document_id]
      ? <span className="cell-stack"><a href={permitLinks.data[row.permit_document_id]} onClick={(event) => event.stopPropagation()} rel="noreferrer" target="_blank">{row.permit_no ?? row.permit_code ?? 'Open'}</a>
          <ValidityBadge compact from={row.permit_valid_from} to={row.permit_valid_to} /></span>
      : row.permit_no ?? row.permit_code ?? '--') },
    { key: 'fsc_claim', label: 'FSC claim', render: (row) => optionLabel(FSC_CLAIMS, row.fsc_claim ?? 'none') },
    { key: 'evidence_state', label: 'Before / after', render: (row) => (
      <button className={`evidence-chip is-${row.evidence_state ?? 'none'}`} onClick={(event) => { event.stopPropagation(); setEvidenceFor(row); }} type="button">
        {row.evidence_state === 'complete' ? 'Evidenced' : row.evidence_state === 'partial' ? 'One image' : 'Add images'}
      </button>
    ) },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
    {
      key: 'submit',
      label: '',
      render: (row) => (row.status === 'draft' && maySubmit ? (
        <button onClick={(event) => { event.stopPropagation(); void submitHarvest(row); }} type="button">
          Submit
        </button>
      ) : null),
    },
  ];

  /**
   * A harvest cannot simply be marked approved: the database refuses any status that is an
   * approval outcome unless a matching request exists, so submission goes through the
   * approval engine and the status follows the decision.
   */
  function openTree(row: InventoryRow) {
    if (row.status !== 'draft') { toast.info('Only a draft tree count can be changed. Record a new count instead.'); return; }
    clear();
    setPlotId(row.plot_id);
    setEditingTree(row);
    treeForm.load(row as unknown as Record<string, unknown>);
    setDrawer('tree');
  }

  async function submitInventory(row: InventoryRow) {
    try {
      await callRpc('fn_submit_for_approval', {
        p_entity_table: 'ops.plot_species_inventories',
        p_entity_id: row.id,
        p_workflow_code: 'INVENTORY_APPROVE',
        p_entity_label: `${row.species_label ?? 'Tree count'} - ${plots.data?.find((plot) => plot.id === row.plot_id)?.label ?? ''}`.trim(),
      });
      await queryClient.invalidateQueries({ queryKey: ['plot_species_inventories'] });
      toast.success('Sent for approval.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be submitted.');
    }
  }

  async function submitHarvest(row: HarvestRow) {
    try {
      await callRpc('fn_submit_for_approval', {
        p_entity_table: 'ops.plot_harvest_entries',
        p_entity_id: row.id,
        p_workflow_code: 'HARVEST_APPROVE',
        p_entity_label: row.harvest_code ?? 'Harvest',
      });
      await queryClient.invalidateQueries({ queryKey: ['plot_harvest_entries'] });
      toast.success('Sent for approval.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be submitted.');
    }
  }

  async function handleSave() {
    if (!plotId) { toast.error('Choose a plot first.'); return; }
    setSaving(true);
    try {
      if (drawer === 'tree') {
        const values = await treeForm.values(validateTree);
        if (!values) return;
        const thisYear = new Date().getFullYear();
        const rows = expandLines(treeFields, values).map((row) => {
          const counted = row.tree_count as number | null;
          const surviving = row.survival_count as number | null;
          const planted = row.plantation_year as number | null;
          return {
            ...row,
            plot_id: plotId,
            // Worked out, not typed twice: age from the year planted, loss from counted less surviving.
            estimated_age_years: planted ? Math.max(thisYear - planted, 0) : null,
            mortality_count: counted !== null && surviving !== null ? Math.max(counted - surviving, 0) : null,
            // A volume species with no unit chosen takes the species' usual one.
            volume_unit_id: row.volume_unit_id ?? measureOf(String(row.species_id))?.volume_unit_id ?? null,
          };
        });
        if (editingTree) await saveTree.mutateAsync({ id: editingTree.id, values: rows[0]! });
        else {
          let done = 0;
          try {
            for (const row of rows) { await saveTree.mutateAsync({ values: row }); done += 1; }
          } catch (error) {
            throw new Error(`${done > 0 ? `Lines 1-${done} were saved; line ${done + 1} was not: ` : ''}${error instanceof Error ? error.message : 'it could not be saved.'}`);
          }
          if (rows.length > 1) toast.success(`${rows.length} species lines recorded.`);
        }
      } else if (drawer === 'asset') {
        if (!check({ name: asset.name.trim() ? undefined : 'Give the asset a name.' })) return;
        await saveAsset.mutateAsync({ values: {
          plot_id: plotId,
          feature_kind: 'asset',
          name: asset.name.trim(),
          type_id: asset.type_id || null,
          description: asset.description.trim() || null,
          quantity: asset.quantity ? Number(asset.quantity) : null,
          quantity_unit_id: asset.quantity_unit_id || null,
          condition_status: asset.condition_status || null,
          acquired_on: asset.acquired_on || null,
          estimated_value: asset.estimated_value ? Number(asset.estimated_value) : null,
        } });
      } else if (drawer === 'harvest') {
        if (!check({
          species_id: harvest.species_id ? undefined : 'Which species?',
          harvest_date: harvest.harvest_date ? undefined : 'When was it harvested?',
          trees_harvested: harvestByVolume || (harvest.trees_harvested && Number(harvest.trees_harvested) > 0)
            ? undefined : 'How many trees were taken?',
          estimated_volume: !harvestByVolume || Number(harvest.estimated_volume) > 0 ? undefined : 'This species is measured by volume: give the volume taken.',
          volume_unit_id: !harvestByVolume || harvest.volume_unit_id ? undefined : 'Which unit is the volume in?',
          quantity_unit_id: !harvest.quantity || harvest.quantity_unit_id ? undefined : 'Which unit is the quantity in?',
          permit_valid_to: !permitFile || harvest.permit_id ? undefined : !harvest.permit_valid_to ? 'Until when is the permit valid?'
            : harvest.permit_valid_from && harvest.permit_valid_to < harvest.permit_valid_from ? 'The permit cannot end before it starts.' : undefined,
        })) return;
        let permitId: string | null = null;
        if (permitFile && !harvest.permit_id && profile?.organization) {
          const uploaded = await uploadDocument({ file: permitFile, bucket: 'plot-evidence', organizationId: profile.organization.id,
                                                  ownerId: plotId, issuedDate: harvest.harvest_date || null });
          permitId = uploaded.id;
        }
        await saveHarvest.mutateAsync({ values: {
          plot_id: plotId,
          species_id: harvest.species_id,
          species_inventory_id: harvest.species_inventory_id || null,
          quantity: harvest.quantity ? Number(harvest.quantity) : null,
          quantity_unit_id: harvest.quantity ? harvest.quantity_unit_id || null : null,
          // Under a permit on record, its dates and document are copied onto the harvest by the database.
          permit_id: harvest.permit_id || null,
          internal_permit_id: harvest.internal_permit_id || null,
          permit_document_id: permitId,
          permit_valid_from: permitId ? harvest.permit_valid_from || harvest.harvest_date || null : null,
          permit_valid_to: permitId ? harvest.permit_valid_to || null : null,
          harvest_date: harvest.harvest_date,
          harvest_type: harvest.harvest_type,
          trees_harvested: harvest.trees_harvested ? Number(harvest.trees_harvested) : 0,
          estimated_volume: harvest.estimated_volume ? Number(harvest.estimated_volume) : null,
          volume_unit_id: harvest.volume_unit_id || null,
          remarks: harvest.remarks.trim() || null,
          fsc_claim: harvest.fsc_claim || null,
        } });
      }
      await queryClient.invalidateQueries({ queryKey: ['plot_stock_summary'] });
      setDrawer(null);
      setEditingTree(null);
      setPermitFile(null);
      clear();
    } catch (error) {
      // The stock guard raises a sentence written for a person ("Harvest of 400 tree(s)
      // exceeds the standing stock: 420 standing, 100 already harvested"), so it is shown as is.
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  const activeQuery = tab === 'stock' ? stock : tab === 'trees' ? trees : tab === 'assets' ? assets : tab === 'species' ? measures : harvests;

  return (
    <LiveScreen
      actions={
        tab === 'harvest'
          ? <AddButton disabled={!mayWriteHarvest || !plotId} label="Record harvest"
                       onClick={() => { clear(); setDrawer('harvest'); }}
                       title={!plotId ? 'Choose a plot first.' : mayWriteHarvest ? undefined : 'Your role cannot record harvests.'} />
          : tab === 'assets'
            ? <AddButton disabled={!plotId} label="Mark asset on map"
                         onClick={() => {
                           // Assets are marked WHERE they are, on the plot's map - which now opens on
                           // Risk assessment, their own register (owner, 2026-09-27).
                           navigate(`/risk-assessment?mark=${encodeURIComponent(plotId)}`);
                         }}
                         title={!plotId ? 'Choose a plot first.' : 'Marked on the plot\'s map, on Risk assessment'} />
            // Offered on Stock as well as Trees (owner, 2026-09-27: "option to add inventory of trees is
            // missing?"): Stock is where the page opens. With no plot chosen above, the form asks.
            : tab === 'trees' || tab === 'stock'
              ? <AddButton disabled={!mayWriteInventory} label="Add tree count"
                           onClick={() => { clear(); setEditingTree(null); treeForm.load(null); setDrawer('tree'); }}
                           title={mayWriteInventory ? undefined : 'Your role cannot record tree counts.'} />
              : undefined
      }
      subtitle="Standing trees, assets on the plot, and what has been harvested. Net stock is derived, never typed."
      title="Inventory and stock"
    >
      <div className="toolbar-row">
        <div style={{ minWidth: 280 }}>
          <Select
            onChange={setPlotId}
            options={plots.data ?? []}
            placeholder={plots.isLoading ? 'Loading plots...' : 'All plots in your scope'}
            value={plotId}
          />
        </div>
      </div>

      <div className="tab-row" role="tablist">
        {TABS.map((entry) => (
          <button aria-selected={tab === entry.id} className={tab === entry.id ? 'active' : ''}
                  key={entry.id} onClick={() => { setTab(entry.id); setStat(null); }} role="tab" type="button">
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'stock' && (
        <div className="tile-row" style={{ marginTop: 14 }}>
          <div className="tile"><span>Net standing trees</span><strong>{formatNumber(totals.standing)}</strong></div>
          <div className="tile"><span>Harvested to date</span><strong>{formatNumber(totals.harvested)}</strong></div>
          <div className="tile"><span>Net volume</span><strong>{formatNumber(totals.volume, 2)}</strong></div>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        {tab === 'trees' && <StatCards active={stat} onSelect={setStat} rows={trees.data ?? []} secondary={{ sum: (row) => row.tree_count, unit: 'trees', digits: 0 }} stats={TREE_STATS} />}
        {tab === 'assets' && <StatCards active={stat} onSelect={setStat} rows={assets.data ?? []} stats={ASSET_STATS} />}
        {tab === 'harvest' && <StatCards active={stat} onSelect={setStat} rows={harvests.data ?? []} secondary={{ sum: (row) => row.trees_harvested, unit: 'trees', digits: 0 }} stats={HARVEST_STATS} />}
        <QueryState
          empty={tab === 'stock'
            ? 'No stock yet. Record a tree inventory against a plot and it appears here.'
            : 'Nothing recorded yet.'}
          query={activeQuery}
        >
          {/*
            * One grid per tab, and only the open one is mounted - so exactly one publishes its
            * search, columns, selection and export to the page header at a time.
            *
            * Net stock has no `deleteView`: its rows are a COMPUTED roll-up of species against
            * plots with a synthetic id, not records, so there is nothing there to delete.
            */}
          {tab === 'stock' && (
            <DataGrid columns={stockColumns} exportName="net-stock" recordNoun="line"
                      rows={(stock.data ?? []).map((row, index) => ({ ...row, id: `${row.plot_id}-${row.species_id}-${index}` }))}
                      screenKey="plot.inventory" searchPlaceholder="Plot, species or farmer" />
          )}
          {tab === 'trees' && (
            <DataGrid columns={treeColumns} deleteView="plot_species_inventories" exportName="tree-inventory"
                      onRowClick={openTree} recordNoun="species line" rows={applyStat(trees.data ?? [], TREE_STATS, stat)}
                      screenKey="plot.inventory" searchPlaceholder="Plot, species or farmer" />
          )}
          {tab === 'assets' && (
            <DataGrid columns={assetColumns} exportName="asset-inventory" recordNoun="asset"
                      rows={applyStat(assets.data ?? [], ASSET_STATS, stat)}
                      screenKey="plot.inventory" searchPlaceholder="Plot, asset or type" />
          )}
          {tab === 'harvest' && (
            <DataGrid columns={harvestColumns} deleteView="plot_harvest_entries" exportName="harvests"
                      onRowClick={setEvidenceFor} recordNoun="harvest"
                      rows={applyStat(harvests.data ?? [], HARVEST_STATS, stat)}
                      screenKey="plot.harvest" searchPlaceholder="Plot, species or claim" />
          )}
        </QueryState>
        {tab === 'species' && (
          <div className="species-measures">
            <p className="muted" style={{ margin: '0 0 8px' }}>
              How each species is measured. Counted species need a tree count; volume species need a volume and unit,
              and their harvests are checked against the standing volume. {isAdmin(profile) ? '' : 'Only a Project Admin can change this.'}
            </p>
            <table className="simple-table">
              <thead><tr><th>Common name</th><th>Scientific name</th><th>Measured by</th><th>Usual volume unit</th><th>Production unit</th></tr></thead>
              <tbody>
                {(measures.data ?? []).map((row) => (
                  <tr key={row.id}>
                    <td><SpeciesNameCell disabled={!isAdmin(profile)} key={`c${row.id}${row.common_name}`} onSave={(value) => void saveSpecies(row, value, row.scientific_name ?? '')}
                                         placeholder="Common name" value={row.common_name ?? row.label} /></td>
                    <td><SpeciesNameCell disabled={!isAdmin(profile)} italic key={`s${row.id}${row.scientific_name}`} onSave={(value) => void saveSpecies(row, row.common_name ?? row.label, value)}
                                         placeholder="e.g. Tectona grandis" value={row.scientific_name ?? ''} /></td>
                    <td>
                      <div aria-label={`Measure for ${row.label}`} className="view-toggle member-type" role="group">
                        {(['count', 'volume'] as const).map((measure) => (
                          <button aria-pressed={row.measure_by === measure} disabled={!isAdmin(profile)} key={measure}
                                  onClick={() => void setMeasure(row, measure)} type="button">
                            {measure === 'count' ? 'Count' : 'Volume'}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td style={{ minWidth: 180 }}>
                      {row.measure_by === 'volume'
                        ? <Select disabled={!isAdmin(profile)} onChange={(value) => void setMeasure(row, 'volume', value)}
                                  options={volumeOptions} placeholder="Select unit" value={row.volume_unit_id ?? ''} />
                        : <span className="muted">--</span>}
                    </td>
                    <td style={{ minWidth: 160 }}>
                      <Select disabled={!isAdmin(profile)} onChange={(value) => void setProductionUnit(row, value)}
                              options={quantityOptions} placeholder="Not set" value={row.production_unit_id ?? ''} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {isAdmin(profile) && (
              <div className="inline-form" style={{ marginTop: 10 }}>
                <div className="field-row">
                  <Field label="New species - common name">
                    <input onChange={(event) => setNewSpecies({ ...newSpecies, common: event.target.value })} placeholder="e.g. Arjun" value={newSpecies.common} />
                  </Field>
                  <Field label="Scientific name">
                    <input onChange={(event) => setNewSpecies({ ...newSpecies, scientific: event.target.value })} placeholder="e.g. Terminalia arjuna" value={newSpecies.scientific} />
                  </Field>
                </div>
                <div className="inline-form-actions">
                  <button className="primary" disabled={!newSpecies.common.trim()} onClick={() => void addSpecies()} type="button">Add species</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <RecordDrawer
        record={drawer === 'tree' && editingTree ? { table: 'ops.plot_species_inventories', id: editingTree.id, invalidate: ['plot_species_inventories'] } : null}
        footer={
          <>
            <button className="cancel" onClick={() => setDrawer(null)} type="button">Cancel</button>
            <button className="primary" disabled={saving} onClick={() => void handleSave()} type="button">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
        onClose={() => setDrawer(null)}
        open={drawer !== null}
        subtitle={plots.data?.find((plot) => plot.id === plotId)?.label}
        sections={drawer === 'tree' ? treeForm.drawerSections() : undefined}
        title={drawer === 'tree' ? (editingTree ? 'Edit tree count' : 'Tree inventory') : drawer === 'asset' ? 'Plot asset' : 'Harvest entry'}
        wide={drawer === 'tree'}
      >
        {drawer === 'tree' && (
          <>
            {!editingTree && treeForm.drawerSections()?.active === 'General' && (
              <Field error={errors.plot_id} hint="The plot the trees stand on." label="Plot" required>
                <Select onChange={setPlotId} options={plots.data ?? []} placeholder="Select the plot" value={plotId} />
              </Field>
            )}
            {treeForm.render(true, true)}
          </>
        )}

        {drawer === 'asset' && (
          <>
            <Field error={errors.name} label="Asset" required>
              <input onChange={(event) => setAsset({ ...asset, name: event.target.value })}
                     placeholder="Irrigation pump, fencing, storage shed" value={asset.name} />
            </Field>
            <Field label="Description">
              <textarea onChange={(event) => setAsset({ ...asset, description: event.target.value })} rows={2} value={asset.description} />
            </Field>
            <div className="field-row">
              <Field label="Quantity">
                <input inputMode="decimal" onChange={(event) => setAsset({ ...asset, quantity: event.target.value })} value={asset.quantity} />
              </Field>
              <Field label="Unit">
                <Select onChange={(value) => setAsset({ ...asset, quantity_unit_id: value })} options={quantityOptions} value={asset.quantity_unit_id} />
              </Field>
            </div>
            <div className="field-row">
              <Field label="Condition">
                <Select onChange={(value) => setAsset({ ...asset, condition_status: value })} options={CONDITION} value={asset.condition_status} />
              </Field>
              <Field label="Acquired on">
                <input onChange={(event) => setAsset({ ...asset, acquired_on: event.target.value })} type="date" value={asset.acquired_on} />
              </Field>
            </div>
            <Field label="Estimated value">
              <input inputMode="decimal" onChange={(event) => setAsset({ ...asset, estimated_value: event.target.value })} value={asset.estimated_value} />
            </Field>
          </>
        )}

        {drawer === 'harvest' && (
          <>
            <Field error={errors.species_id} label="Species" required>
              <Select onChange={(value) => pickSpecies('harvest', value)} options={speciesOptions} value={harvest.species_id} />
            </Field>
            <div className="field-row">
              <Field error={errors.harvest_date} label="Harvest date" required>
                <input onChange={(event) => setHarvest({ ...harvest, harvest_date: event.target.value })} type="date" value={harvest.harvest_date} />
              </Field>
              <Field label="Type">
                <Select onChange={(value) => setHarvest({ ...harvest, harvest_type: value })} options={HARVEST_TYPE} value={harvest.harvest_type} />
              </Field>
            </div>
            <Field error={errors.trees_harvested}
                   hint={harvestByVolume ? 'Optional for a volume species.' : 'Checked against the standing stock on save.'}
                   label={harvestByVolume ? 'Trees / clumps (optional)' : 'Trees harvested'} required={!harvestByVolume}>
              <input inputMode="numeric" onChange={(event) => setHarvest({ ...harvest, trees_harvested: event.target.value })} value={harvest.trees_harvested} />
            </Field>
            <div className="field-row">
              <Field error={errors.estimated_volume} hint={harvestByVolume ? 'Checked against the standing volume on save.' : undefined}
                     label={harvestByVolume ? 'Volume harvested' : 'Estimated volume'} required={harvestByVolume}>
                <input inputMode="decimal" onChange={(event) => setHarvest({ ...harvest, estimated_volume: event.target.value })} value={harvest.estimated_volume} />
              </Field>
              <Field error={errors.volume_unit_id} label="Volume unit" required={harvestByVolume}>
                <Select onChange={(value) => setHarvest({ ...harvest, volume_unit_id: value })} options={volumeOptions} value={harvest.volume_unit_id} />
              </Field>
            </div>
            <Field hint="The count this harvest is taken from. The latest is chosen for you." label="From tree count">
              <Select onChange={(value) => setHarvest({ ...harvest, species_inventory_id: value })}
                      options={[{ id: '', label: '--' }, ...inventoryOptions]}
                      placeholder={!harvest.species_id ? 'Choose the species first' : harvestInventories.isLoading ? 'Loading...' : 'Select tree count'}
                      value={harvest.species_inventory_id} />
            </Field>
            <div className="field-row">
              <Field hint="What it yielded - weight, logs or volume." label="Quantity">
                <input inputMode="decimal" onChange={(event) => setHarvest({ ...harvest, quantity: event.target.value })} value={harvest.quantity} />
              </Field>
              <Field error={errors.quantity_unit_id} label="Quantity unit" required={Boolean(harvest.quantity)}>
                <Select onChange={(value) => setHarvest({ ...harvest, quantity_unit_id: value })} options={quantityOptions} value={harvest.quantity_unit_id} />
              </Field>
            </div>
            <Field hint={internalPermitOptions.length ? 'The group\'s approval to fell; the harvest report is written against it.'
                     : 'No approved internal permit for this plot: add one on Harvest permits (internal).'} label="Internal harvest permit">
              <Select onChange={(value) => setHarvest({ ...harvest, internal_permit_id: value })} options={internalPermitOptions}
                      placeholder={internalPermitOptions.length ? 'Select the internal permit' : 'No internal permit on record'} value={harvest.internal_permit_id} />
            </Field>
            <Field hint={permitOptions.length ? 'The harvest must fall inside the permit\'s dates, species and trees.'
                     : 'No granted permit for this farmer yet: add one on Harvest permits, or attach a copy below.'} label="FD permit">
              <Select onChange={(value) => setHarvest({ ...harvest, permit_id: value })} options={permitOptions}
                      placeholder={permitOptions.length ? 'Select the permit' : 'No permit on record'} value={harvest.permit_id} />
            </Field>
            {!harvest.permit_id && (
              <Field hint={permitFile ? `${permitFile.name} will be uploaded.` : 'A permit not on record: a photo or PDF of it.'} label="Permit copy">
                <DropZone accept="image/*,application/pdf" buttonLabel={permitFile ? 'Change file' : 'Choose file'}
                          label="Drag the permit here, or" onFile={setPermitFile} />
              </Field>
            )}
            {permitFile && !harvest.permit_id && (
              <div className="field-row">
                <Field hint="Blank: the harvest date." label="Permit valid from">
                  <input onChange={(event) => setHarvest({ ...harvest, permit_valid_from: event.target.value })} type="date" value={harvest.permit_valid_from} />
                </Field>
                <Field error={errors.permit_valid_to} label="Permit valid to" required>
                  <input onChange={(event) => setHarvest({ ...harvest, permit_valid_to: event.target.value })} type="date" value={harvest.permit_valid_to} />
                </Field>
              </div>
            )}
            <Field hint={`Blank: the plot's claim (${optionLabel(FSC_CLAIMS, plotClaim)}). A harvest can claim no more than its plot; FSC 100% only on or after the CB approval date.`}
                   label="FSC claim">
              <Select onChange={(value) => setHarvest({ ...harvest, fsc_claim: value })}
                      options={FSC_CLAIMS.filter((claim) => claim.id === 'none' || claim.id === plotClaim || (plotClaim === 'fsc_100' && claim.id === 'controlled_wood'))}
                      placeholder="As the plot" value={harvest.fsc_claim} />
            </Field>
            <Field label="Remarks">
              <textarea onChange={(event) => setHarvest({ ...harvest, remarks: event.target.value })} rows={2} value={harvest.remarks} />
            </Field>
          </>
        )}
      </RecordDrawer>
      {evidenceFor && <HarvestEvidence harvest={evidenceFor} key={evidenceFor.id} onClose={() => setEvidenceFor(null)} />}
    </LiveScreen>
  );
}

/** An inline name cell: edits in place and saves when it loses focus. */
function SpeciesNameCell({ value, onSave, disabled, placeholder, italic }: {
  value: string; onSave: (value: string) => void; disabled?: boolean; placeholder?: string; italic?: boolean;
}) {
  const [text, setText] = useState(value);
  return (
    <input disabled={disabled} onBlur={() => { if (text.trim() !== value.trim()) onSave(text); }}
           onChange={(event) => setText(event.target.value)} placeholder={placeholder}
           style={italic ? { fontStyle: 'italic' } : undefined} value={text} />
  );
}
